// All secrets stay on this side of the control service. These are wire formats;
// changing a header, KDF parameter or AAD requires a new version, not a fallback.
export type ObjectScope = {
  datasetId: string;
  generation: string;
  keyVersion: number;
  objectId: string;
  format: 2;
};
export type CipherObject = { header: ObjectScope; iv: string; ciphertext: string };
export type RecoveryPayload = {
  datasetId: string;
  generation: string;
  keyVersion: number;
  dataKey: string;
  recoverySecret: string;
  recoveryPrivateKey: JsonWebKey;
};
export const RECOVERY_ITERATIONS = 600_000;
export const MAX_CIPHER_BYTES = 4 * 1024 * 1024;
const encoder = new TextEncoder();
const decoder = new TextDecoder("utf-8", { fatal: true });

export function canonical(value: unknown): string {
  if (value === null || typeof value === "string" || typeof value === "boolean") return JSON.stringify(value);
  if (typeof value === "number" && Number.isFinite(value)) return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value)
      .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
      .map(([k, v]) => `${JSON.stringify(k)}:${canonical(v)}`)
      .join(",")}}`;
  throw new Error("sync-format");
}

export function base64url(bytes: Uint8Array): string {
  let binary = "";
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary).replaceAll("+", "-").replaceAll("/", "_").replace(/=+$/, "");
}

export function unbase64url(value: string, max = MAX_CIPHER_BYTES): Uint8Array<ArrayBuffer> {
  if (typeof value !== "string" || !/^[\w-]+$/.test(value) || value.length > Math.ceil((max * 4) / 3))
    throw new Error("sync-format");
  const bytes = Uint8Array.from(atob(value.replaceAll("-", "+").replaceAll("_", "/")), (c) => c.charCodeAt(0));
  if (bytes.length > max || base64url(bytes) !== value) throw new Error("sync-format");
  return bytes;
}

export function newDataKey(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)));
}

export function assertScope(scope: ObjectScope) {
  if (
    Object.keys(scope ?? {})
      .sort()
      .join() !== "datasetId,format,generation,keyVersion,objectId" ||
    !scope ||
    scope.format !== 2 ||
    !Number.isSafeInteger(scope.keyVersion) ||
    scope.keyVersion < 1 ||
    ![scope.datasetId, scope.generation, scope.objectId].every((s) => typeof s === "string" && /^[\w-]{1,128}$/.test(s))
  )
    throw new Error("sync-scope");
}

async function dataKey(material: string | CryptoKey, usage: KeyUsage[]) {
  if (typeof material !== "string") return material;
  const bytes = unbase64url(material, 32);
  if (bytes.length !== 32) throw new Error("sync-key-format");
  try {
    return await crypto.subtle.importKey("raw", bytes, "AES-GCM", false, usage);
  } finally {
    bytes.fill(0);
  }
}

async function sealBytes(key: CryptoKey, header: unknown, bytes: Uint8Array<ArrayBuffer>) {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const cipher = await crypto.subtle.encrypt(
    { name: "AES-GCM", iv, additionalData: encoder.encode(canonical(header)), tagLength: 128 },
    key,
    bytes,
  );
  if (cipher.byteLength > MAX_CIPHER_BYTES) throw new Error("sync-file-limit");
  return { iv: base64url(iv), ciphertext: base64url(new Uint8Array(cipher)) };
}

async function openBytes(key: CryptoKey, header: unknown, cipher: { iv: string; ciphertext: string }) {
  const iv = unbase64url(cipher.iv, 12);
  const bytes = unbase64url(cipher.ciphertext);
  if (iv.length !== 12 || bytes.length < 16) throw new Error("sync-format");
  try {
    return new Uint8Array(
      await crypto.subtle.decrypt(
        { name: "AES-GCM", iv, additionalData: encoder.encode(canonical(header)), tagLength: 128 },
        key,
        bytes,
      ),
    );
  } catch {
    throw new Error("sync-cipher-invalid");
  }
}

export async function sealObject(
  material: string | CryptoKey,
  header: ObjectScope,
  value: unknown,
): Promise<CipherObject> {
  assertScope(header);
  const bytes = encoder.encode(canonical(value));
  return { header, ...(await sealBytes(await dataKey(material, ["encrypt"]), header, bytes)) };
}

export async function openObject(
  material: string | CryptoKey,
  expected: ObjectScope,
  cipher: CipherObject,
): Promise<unknown> {
  assertScope(expected);
  if (!cipher || Object.keys(cipher).sort().join() !== "ciphertext,header,iv") throw new Error("sync-format");
  assertScope(cipher.header);
  if (canonical(cipher.header) !== canonical(expected)) throw new Error("sync-scope-mismatch");
  const bytes = await openBytes(await dataKey(material, ["decrypt"]), expected, cipher);
  try {
    return JSON.parse(decoder.decode(bytes));
  } catch {
    throw new Error("sync-cipher-invalid");
  } finally {
    bytes.fill(0);
  }
}

export async function createDeviceKeys() {
  return {
    wrapping: await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]),
    signing: await crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, false, ["sign", "verify"]),
    agreement: await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveKey"]),
  };
}

// Only recovery signing material is exportable, while constructing an encrypted kit.
export function createRecoveryKeys() {
  return crypto.subtle.generateKey({ name: "ECDSA", namedCurve: "P-256" }, true, ["sign", "verify"]);
}

export async function sealDeviceEnvelope(recipient: JsonWebKey, scope: ObjectScope, value: unknown) {
  const publicKey = await crypto.subtle.importKey("jwk", recipient, { name: "ECDH", namedCurve: "P-256" }, false, []);
  const ephemeral = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, false, ["deriveKey"]);
  const key = await crypto.subtle.deriveKey(
    { name: "ECDH", public: publicKey },
    ephemeral.privateKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt"],
  );
  return {
    ephemeral: await crypto.subtle.exportKey("jwk", ephemeral.publicKey),
    cipher: await sealObject(key, scope, value),
  };
}

export async function openDeviceEnvelope(
  privateKey: CryptoKey,
  scope: ObjectScope,
  envelope: Awaited<ReturnType<typeof sealDeviceEnvelope>>,
) {
  const publicKey = await crypto.subtle.importKey(
    "jwk",
    envelope.ephemeral,
    { name: "ECDH", namedCurve: "P-256" },
    false,
    [],
  );
  const key = await crypto.subtle.deriveKey(
    { name: "ECDH", public: publicKey },
    privateKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["decrypt"],
  );
  return openObject(key, scope, envelope.cipher);
}

export async function wrapDataKey(wrapping: CryptoKey, scope: ObjectScope, material: string) {
  assertScope(scope);
  const bytes = unbase64url(material, 32);
  if (bytes.length !== 32) throw new Error("sync-key-format");
  try {
    return { header: scope, ...(await sealBytes(wrapping, scope, bytes)) };
  } finally {
    bytes.fill(0);
  }
}

export async function unwrapDataKey(wrapping: CryptoKey, scope: ObjectScope, cipher: CipherObject) {
  const bytes = await unwrapDataKeyBytes(wrapping, scope, cipher);
  try {
    return await crypto.subtle.importKey("raw", bytes, "AES-GCM", false, ["encrypt", "decrypt"]);
  } finally {
    bytes.fill(0);
  }
}

// A plaintext copy exists only during an explicitly approved pairing/recovery/rotation.
export async function unwrapDataKeyBytes(wrapping: CryptoKey, scope: ObjectScope, cipher: CipherObject) {
  assertScope(scope);
  if (canonical(scope) !== canonical(cipher.header)) throw new Error("sync-scope-mismatch");
  const bytes = await openBytes(wrapping, scope, cipher);
  if (bytes.length !== 32) throw new Error("sync-key-format");
  return bytes;
}

export async function signChallenge(privateKey: CryptoKey, challenge: unknown) {
  return base64url(
    new Uint8Array(
      await crypto.subtle.sign({ name: "ECDSA", hash: "SHA-256" }, privateKey, encoder.encode(canonical(challenge))),
    ),
  );
}

export async function verifyChallenge(publicKey: CryptoKey, challenge: unknown, signature: string) {
  try {
    return await crypto.subtle.verify(
      { name: "ECDSA", hash: "SHA-256" },
      publicKey,
      unbase64url(signature, 80),
      encoder.encode(canonical(challenge)),
    );
  } catch {
    return false;
  }
}

type RecoveryHeader = {
  type: "classroom-sync-recovery";
  version: 1;
  datasetId: string;
  kdf: "PBKDF2-SHA256";
  iterations: 600000;
  salt: string;
  cipher: "AES-256-GCM";
};
async function passwordKey(password: string, header: RecoveryHeader) {
  // Count Unicode code points; do not trim or normalise a password.
  if (typeof password !== "string" || [...password].length < 15 || encoder.encode(password).length > 4096)
    throw new Error("sync-password-short");
  const secret = encoder.encode(password);
  try {
    const base = await crypto.subtle.importKey("raw", secret, "PBKDF2", false, ["deriveKey"]);
    return await crypto.subtle.deriveKey(
      { name: "PBKDF2", hash: "SHA-256", iterations: RECOVERY_ITERATIONS, salt: unbase64url(header.salt, 32) },
      base,
      { name: "AES-GCM", length: 256 },
      false,
      ["encrypt", "decrypt"],
    );
  } finally {
    secret.fill(0);
  }
}

function assertRecoveryPayload(payload: RecoveryPayload) {
  assertScope({
    datasetId: payload.datasetId,
    generation: payload.generation,
    keyVersion: payload.keyVersion,
    objectId: "recovery",
    format: 2,
  });
  for (const material of [payload.dataKey, payload.recoverySecret])
    if (unbase64url(material, 32).length !== 32) throw new Error("sync-recovery-format");
  const key = payload.recoveryPrivateKey;
  if (!key || key.kty !== "EC" || key.crv !== "P-256" || !key.d || !key.x || !key.y)
    throw new Error("sync-recovery-format");
}

export async function createRecoveryFile(payload: RecoveryPayload, password: string): Promise<string> {
  assertRecoveryPayload(payload);
  const header: RecoveryHeader = {
    type: "classroom-sync-recovery",
    version: 1,
    datasetId: payload.datasetId,
    kdf: "PBKDF2-SHA256",
    iterations: RECOVERY_ITERATIONS,
    salt: newDataKey(),
    cipher: "AES-256-GCM",
  };
  const { dataKey: material, ...recovery } = payload;
  const keyScope: ObjectScope = {
    datasetId: payload.datasetId,
    generation: payload.generation,
    keyVersion: payload.keyVersion,
    objectId: "recovery-data-key",
    format: 2,
  };
  const bytes = encoder.encode(
    canonical({ ...recovery, wrappedDataKey: await sealObject(payload.recoverySecret, keyScope, material) }),
  );
  try {
    return canonical({ header, ...(await sealBytes(await passwordKey(password, header), header, bytes)) });
  } finally {
    bytes.fill(0);
  }
}

export async function openRecoveryFile(file: string, password: string, datasetId?: string): Promise<RecoveryPayload> {
  if (typeof file !== "string" || encoder.encode(file).length > 32 * 1024) throw new Error("sync-recovery-format");
  const value = JSON.parse(file);
  const h = value?.header as RecoveryHeader;
  if (
    !h ||
    Object.keys(value).sort().join() !== "ciphertext,header,iv" ||
    Object.keys(h).sort().join() !== "cipher,datasetId,iterations,kdf,salt,type,version" ||
    h.type !== "classroom-sync-recovery" ||
    h.version !== 1 ||
    h.kdf !== "PBKDF2-SHA256" ||
    h.iterations !== RECOVERY_ITERATIONS ||
    h.cipher !== "AES-256-GCM" ||
    unbase64url(h.salt, 32).length !== 32
  )
    throw new Error("sync-recovery-format");
  const bytes = await openBytes(await passwordKey(password, h), h, value);
  try {
    const contents = JSON.parse(decoder.decode(bytes));
    if (
      !contents ||
      Object.keys(contents).sort().join() !==
        "datasetId,generation,keyVersion,recoveryPrivateKey,recoverySecret,wrappedDataKey"
    )
      throw new Error("sync-recovery-format");
    const { wrappedDataKey, ...recovery } = contents;
    const material = await openObject(
      recovery.recoverySecret,
      {
        datasetId: recovery.datasetId,
        generation: recovery.generation,
        keyVersion: recovery.keyVersion,
        objectId: "recovery-data-key",
        format: 2,
      },
      wrappedDataKey,
    );
    if (typeof material !== "string") throw new Error("sync-recovery-format");
    const payload = { ...recovery, dataKey: material } as RecoveryPayload;
    assertRecoveryPayload(payload);
    if (payload.datasetId !== h.datasetId || (datasetId !== undefined && payload.datasetId !== datasetId))
      throw new Error("sync-dataset-mismatch");
    return payload;
  } finally {
    bytes.fill(0);
  }
}
